/**
 * useTenant: resolve the tenant slug from the hostname, fetch its public
 * configuration from GET /v1/tenant, and mirror the result into the
 * Zustand tenant store.
 *
 * Graceful degradation:
 *  - Unresolvable hostname (localhost without override) → thrown error
 *    surfaced as an ErrorState with guidance.
 *  - Backend unreachable → query fails after retries; the store keeps the
 *    last-known-good config and the hook reports `isOffline` so the UI can
 *    show fallback branding instead of a blank shell.
 *  - Unknown tenant (404) → friendly "not found" state.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, fetchTenant, type TenantConfig } from '../lib/api';
import { resolveTenantSlugFromWindow } from '../lib/tenant';
import { useTenantStore } from '../stores/tenant';

export interface UseTenantResult {
  /** Resolved tenant config, or the last-known-good one if offline. */
  tenant: TenantConfig | null;
  slug: string | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  errorMessage: string | null;
  /** True when the query failed but we have a cached config to show. */
  isOfflineWithCache: boolean;
  /** True when the slug was resolved but no tenant exists for it (404). */
  isUnknownTenant: boolean;
  refetch: () => void;
}

function toErrorMessage(slug: string, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isNetworkError) {
      return (
        `Could not reach the backend API while loading the "${slug}" site. ` +
        'Check your connection, or that VITE_API_URL points at a running backend.'
      );
    }
    if (error.isNotFound) {
      return (
        `No redistricting site is configured for "${slug}". ` +
        'Check the subdomain, or ask your administrator to create the tenant.'
      );
    }
    return `The "${slug}" site could not be loaded (${error.code}).`;
  }
  return error instanceof Error ? error.message : 'The site could not be loaded.';
}

export function useTenant(): UseTenantResult {
  const resolution = useMemo(() => {
    try {
      return { ok: true as const, value: resolveTenantSlugFromWindow() };
    } catch (error) {
      return { ok: false as const, error };
    }
  }, []);

  const setLoading = useTenantStore((s) => s.setLoading);
  const setReady = useTenantStore((s) => s.setReady);
  const setError = useTenantStore((s) => s.setError);
  const storeTenant = useTenantStore((s) => s.tenant);
  const storeStatus = useTenantStore((s) => s.status);
  const storeSlug = useTenantStore((s) => s.slug);
  const storeErrorMessage = useTenantStore((s) => s.errorMessage);

  const slug = resolution.ok ? resolution.value.slug : null;

  const query = useQuery({
    queryKey: ['tenant', slug],
    queryFn: () => {
      if (!slug) throw new Error('No tenant slug resolved');
      return fetchTenant(slug);
    },
    enabled: resolution.ok && slug !== null,
    // Tenant config changes rarely; cache aggressively. The backend's
    // config_version can be used later to invalidate explicitly.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });

  useEffect(() => {
    if (!resolution.ok) {
      setError('unknown', toErrorMessage('unknown', resolution.error));
      return;
    }
    if (slug) setLoading(slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (query.data && slug) {
      setReady(query.data);
    }
  }, [query.data, slug, setReady]);

  useEffect(() => {
    if (query.error && slug) {
      setError(slug, toErrorMessage(slug, query.error));
    }
  }, [query.error, slug, setError]);

  const isUnknownTenant = query.error instanceof ApiError && query.error.isNotFound;

  return {
    tenant: storeTenant,
    slug: storeSlug,
    status: storeStatus,
    errorMessage: storeErrorMessage,
    isOfflineWithCache:
      query.isError && !(query.error instanceof ApiError && query.error.isNotFound) && storeTenant !== null,
    isUnknownTenant,
    refetch: () => {
      void query.refetch();
    },
  };
}
